import { couponCollectionFragment } from "@saleor/fragments/couponCollection";
import { couponFragment } from "@saleor/fragments/coupons";
import { couponErrorFragment } from "@saleor/fragments/errors";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import {
  AssignCouponToCollection,
  AssignCouponToCollectionVariables
} from "./types/AssignCouponToCollection";
import {
  CouponActivate,
  CouponActivateVariables
} from "./types/CouponActivate";
import {
  CouponCollectionCreate,
  CouponCollectionCreateVariables
} from "./types/CouponCollectionCreate";
import {
  CouponCollectionGenerateDynamicLink,
  CouponCollectionGenerateDynamicLinkVariables
} from "./types/CouponCollectionGenerateDynamicLink";
import {
  CouponCollectionToggleIsFeatured,
  CouponCollectionToggleIsFeaturedVariables
} from "./types/CouponCollectionToggleIsFeatured";
import {
  CouponCollectionUpdate,
  CouponCollectionUpdateVariables
} from "./types/CouponCollectionUpdate";
import { CouponCreate, CouponCreateVariables } from "./types/CouponCreate";
import {
  CouponDeactivate,
  CouponDeactivateVariables
} from "./types/CouponDeactivate";
import {
  CouponGenerateDynamicLink,
  CouponGenerateDynamicLinkVariables
} from "./types/CouponGenerateDynamicLink";
import {
  CouponManualReorder,
  CouponManualReorderVariables
} from "./types/CouponManualReorder";
import {
  CouponPresetReorder,
  CouponPresetReorderVariables
} from "./types/CouponPresetReorder";
import {
  CouponToggleIsFeatured,
  CouponToggleIsFeaturedVariables
} from "./types/CouponToggleIsFeatured";
import { CouponUpdate, CouponUpdateVariables } from "./types/CouponUpdate";
import {
  DraftCouponCreate,
  DraftCouponCreateVariables
} from "./types/DraftCouponCreate";
import {
  DraftCouponUpdate,
  DraftCouponUpdateVariables
} from "./types/DraftCouponUpdate";
import {
  RemoveCouponToCollection,
  RemoveCouponToCollectionVariables
} from "./types/RemoveCouponToCollection";
import {
  SetCouponToCollection,
  SetCouponToCollectionVariables
} from "./types/SetCouponToCollection";
import {
  ToggleCouponCollectionActivation,
  ToggleCouponCollectionActivationVariables
} from "./types/ToggleCouponCollectionActivation";

export const couponCreateMutation = gql`
  ${couponFragment}
  ${couponErrorFragment}
  mutation CouponCreate(
    $input: CouponCreateInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    couponCreate(input: $input) {
      coupon {
        ...CouponFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponCreate = makeMutation<
  CouponCreate,
  CouponCreateVariables
>(couponCreateMutation);

export const draftCouponCreateMutation = gql`
  ${couponFragment}
  ${couponErrorFragment}
  mutation DraftCouponCreate(
    $input: DraftCouponCreateInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    draftCouponCreate(input: $input) {
      coupon {
        ...CouponFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useDraftCouponCreate = makeMutation<
  DraftCouponCreate,
  DraftCouponCreateVariables
>(draftCouponCreateMutation);

const draftCouponUpdateMutation = gql`
  ${couponFragment}
  ${couponErrorFragment}
  mutation DraftCouponUpdate(
    $id: ID!
    $input: DraftCouponUpdateInput
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    draftCouponUpdate(id: $id, input: $input) {
      coupon {
        ...CouponFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useDraftCouponUpdate = makeMutation<
  DraftCouponUpdate,
  DraftCouponUpdateVariables
>(draftCouponUpdateMutation);

const couponCreateDeepLink = gql`
  mutation CouponGenerateDynamicLink($id: ID!) {
    couponGenerateDynamicLink(id: $id) {
      discountErrors {
        field
        message
        code
      }
      coupon {
        id
        deeplinkUrl
        shortDynamicLink
      }
    }
  }
`;

export const useCouponCreateDeepLink = makeMutation<
  CouponGenerateDynamicLink,
  CouponGenerateDynamicLinkVariables
>(couponCreateDeepLink);

const couponCollectionCreateDeepLink = gql`
  mutation CouponCollectionGenerateDynamicLink($id: ID!) {
    couponCollectionGenerateDynamicLink(instanceId: $id) {
      discountErrors {
        field
        message
        code
      }
      couponCollection {
        id
        title
        writeup
        classification
        categories
        status
        startDate
        endDate
        bannerImage {
          url
          alt
        }
        isHidden
        deeplinkUrl
        shortDynamicLink
      }
    }
  }
`;

export const useCouponCollectionCreateDeepLink = makeMutation<
  CouponCollectionGenerateDynamicLink,
  CouponCollectionGenerateDynamicLinkVariables
>(couponCollectionCreateDeepLink);

const couponCollectionToggleFeature = gql`
  mutation CouponCollectionToggleIsHidden($id: ID!) {
    couponCollectionToggleIsHidden(instanceId: $id) {
      couponCollection {
        id
        title
        writeup
        classification
        categories
        status
        startDate
        endDate
        bannerImage {
          url
          alt
        }
        isHidden
        deeplinkUrl
        shortDynamicLink
      }
      discountErrors {
        field
        message
        code
      }
    }
  }
`;
export const useCouponCollectionToggleFeature = makeMutation<
  CouponToggleIsFeatured,
  CouponToggleIsFeaturedVariables
>(couponCollectionToggleFeature);

const couponCollectionToggleIsFeatured = gql`
  mutation CouponCollectionToggleIsFeatured($id: ID!) {
    couponCollectionToggleIsFeatured(couponCollectionId: $id) {
      couponCollection {
        id
        isFeatured
      }
      errors {
        field
        message
      }
    }
  }
`;
export const useCouponCollectionToggleIsFeatured = makeMutation<
  CouponCollectionToggleIsFeatured,
  CouponCollectionToggleIsFeaturedVariables
>(couponCollectionToggleIsFeatured);

const couponDeactivate = gql`
  mutation CouponDeactivate($couponId: ID!) {
    couponDeactivate(couponId: $couponId) {
      discountErrors {
        field
        message
        code
      }
      coupon {
        id
        name
        code
        status
      }
    }
  }
`;

export const useCouponDeactivate = makeMutation<
  CouponDeactivate,
  CouponDeactivateVariables
>(couponDeactivate);

const couponActivate = gql`
  mutation CouponActivate($couponId: ID!) {
    couponActivate(couponId: $couponId) {
      discountErrors {
        field
        message
        code
      }
      coupon {
        id
        name
        code
        status
      }
    }
  }
`;
export const useActivateCoupon = makeMutation<
  CouponActivate,
  CouponActivateVariables
>(couponActivate);

const couponUpdate = gql`
  ${couponFragment}
  ${couponErrorFragment}
  mutation CouponUpdate(
    $id: ID!
    $input: CouponUpdateInput!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    couponUpdate(input: $input, id: $id) {
      coupon {
        ...CouponFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponUpdate = makeMutation<
  CouponUpdate,
  CouponUpdateVariables
>(couponUpdate);

const couponToggleFeature = gql`
  ${couponErrorFragment}
  mutation CouponToggleIsFeatured($id: ID!) {
    couponToggleIsFeatured(couponId: $id) {
      coupon {
        id
        code
        name
        isFeatured
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;
export const useCouponToggleFeature = makeMutation<
  CouponToggleIsFeatured,
  CouponToggleIsFeaturedVariables
>(couponToggleFeature);

export const couponManualReorder = gql`
  ${couponErrorFragment}
  mutation CouponManualReorder($couponOrderGroups: [CouponManualReorderInput]) {
    couponManualReorder(couponOrderGroups: $couponOrderGroups) {
      coupons {
        id
        code
        name
        startDate
        endDate
        status
        availableQuantity
        isFeatured
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponManualReorder = makeMutation<
  CouponManualReorder,
  CouponManualReorderVariables
>(couponManualReorder);

export const couponPresetReorder = gql`
  ${couponErrorFragment}
  mutation CouponPresetReorder($input: CouponPresetReorderInput!) {
    couponPresetReorder(input: $input) {
      coupon {
        id
        code
        name
        startDate
        endDate
        status
        availableQuantity
        isFeatured
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponPresetReorder = makeMutation<
  CouponPresetReorder,
  CouponPresetReorderVariables
>(couponPresetReorder);

export const couponCollectionCreateMutation = gql`
  ${couponErrorFragment}
  ${couponCollectionFragment}
  mutation CouponCollectionCreate($input: CouponCollectionCreateInput!) {
    couponCollectionCreate(input: $input) {
      couponCollection {
        ...CouponCollectionFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponCollectionCreate = makeMutation<
  CouponCollectionCreate,
  CouponCollectionCreateVariables
>(couponCollectionCreateMutation);

export const couponCollectionUpdateMutation = gql`
  ${couponErrorFragment}
  ${couponCollectionFragment}
  mutation CouponCollectionUpdate(
    $id: ID!
    $input: CouponCollectionUpdateInput!
  ) {
    couponCollectionUpdate(id: $id, input: $input) {
      couponCollection {
        ...CouponCollectionFragment
      }
      errors: discountErrors {
        ...CouponErrorFragment
      }
    }
  }
`;

export const useCouponCollectionUpdate = makeMutation<
  CouponCollectionUpdate,
  CouponCollectionUpdateVariables
>(couponCollectionUpdateMutation);

export const setCouponToCollection = gql`
  ${couponErrorFragment}
  mutation SetCouponToCollection(
    $coupons: SetCouponsToCollectionInput!
    $instanceId: ID!
  ) {
    setCouponsToCollection(coupons: $coupons, instanceId: $instanceId) {
      errors: discountErrors {
        ...CouponErrorFragment
      }
      couponCollection {
        id
        title
        categories
        coupons(first: 100) {
          totalCount
          edges {
            node {
              id
              code
              name
            }
          }
        }
      }
      assignedCoupons {
        id
        code
        name
      }
      removedCoupons {
        id
        code
        name
      }
    }
  }
`;
export const useSetCouponToCollection = makeMutation<
  SetCouponToCollection,
  SetCouponToCollectionVariables
>(setCouponToCollection);

export const removeCouponToCollection = gql`
  ${couponErrorFragment}
  mutation RemoveCouponToCollection(
    $coupons: SetCouponsToCollectionInput!
    $instanceId: ID!
  ) {
    setCouponsToCollection(coupons: $coupons, instanceId: $instanceId) {
      errors: discountErrors {
        ...CouponErrorFragment
      }
      couponCollection {
        id
        title
        categories
        coupons(first: 100) {
          totalCount
          edges {
            node {
              id
              code
              name
            }
          }
        }
      }
      removedCoupons {
        id
        code
        name
      }
    }
  }
`;
export const useRemoveCouponToCollection = makeMutation<
  RemoveCouponToCollection,
  RemoveCouponToCollectionVariables
>(removeCouponToCollection);

export const assignCouponToCollection = gql`
  ${couponErrorFragment}
  mutation AssignCouponToCollection($couponId: ID!, $instanceId: ID!) {
    setCouponsToCollection(
      coupons: { assign: [$couponId] }
      instanceId: $instanceId
    ) {
      errors: discountErrors {
        ...CouponErrorFragment
      }
      couponCollection {
        id
        title
        categories
        coupons(first: 100) {
          totalCount
          edges {
            node {
              id
              code
              name
            }
          }
        }
      }
      assignedCoupons {
        id
        code
        name
      }
    }
  }
`;
export const useAssignCouponToCollection = makeMutation<
  AssignCouponToCollection,
  AssignCouponToCollectionVariables
>(assignCouponToCollection);

export const toggleCouponCollectionActivation = gql`
  ${couponErrorFragment}
  mutation ToggleCouponCollectionActivation(
    $activate: Boolean!
    $couponCollectionId: ID!
  ) {
    couponCollectionToggleActivate(
      activate: $activate
      couponCollectionId: $couponCollectionId
    ) {
      errors: discountErrors {
        ...CouponErrorFragment
      }
      couponCollection {
        id
        title
        writeup
        classification
        categories
        status
        startDate
        endDate
      }
    }
  }
`;
export const useToggleCouponCollectionActivation = makeMutation<
  ToggleCouponCollectionActivation,
  ToggleCouponCollectionActivationVariables
>(toggleCouponCollectionActivation);

export const couponUnlockCodeBulkCreate = gql`
  mutation CouponUnlockCodeBulkCreate(
    $input: [CouponUnlockCodeBulkCreateInput]!
  ) {
    couponUnlockCodeBulkCreate(input: $input) {
      count
      errors {
        field
        message
      }
    }
  }
`;

export const couponUnlockCodeEdit = gql`
  mutation CouponUnlockCodeUpdate(
    $id: ID!
    $input: CouponUnlockCodeUpdateInput!
  ) {
    couponUnlockCodeUpdate(id: $id, input: $input) {
      couponUnlockCode {
        id
      }
      errors {
        field
        message
      }
    }
  }
`;

export const couponUnlockCodesDelete = gql`
  mutation CouponUnlockCodesDelete($idsToBeDeleted: [ID]!) {
    couponUnlockCodeBulkDelete(ids: $idsToBeDeleted) {
      discountErrors {
        field
        message
      }
    }
  }
`;

export const couponUnlockCodeExportAll = gql`
  mutation couponUnlockCodeExportAll($couponId: ID!) {
    couponUnlockCodeExportAll(couponId: $couponId) {
      fileUrl
      discountErrors {
        field
        message
      }
    }
  }
`;

export const couponUnlockCodeBulkUpload = gql`
  mutation couponUnlockCodeBulkUpload($file: Upload!) {
    couponUnlockCodeBulkUploadAndExtract(file: $file) {
      unlockCodes {
        unlockCode
        usageLimit
        tags
        description
      }
      discountErrors {
        field
        message
        code
      }
    }
  }
`;
